import React, { useEffect, useRef } from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { EVENT_COLORS, EVENT_ICONS } from '@/utils/colors';
import { formatRelative } from '@/utils/formatters';
import type { ActivityEvent } from '@/types/event';

const EVENT_LABELS: Record<string, string> = {
  publication: 'PUB',
  validation_needed: 'VALID',
  sale: 'VENTE',
  alert: 'ALERTE',
  task_done: 'DONE',
  agent_moved: 'MOVE',
  error: 'ERR',
  meeting: 'MEET',
  collaboration: 'COLLAB',
  system: 'SYS',
  seo: 'SEO',
  customer: 'SAV',
};

const EventRow: React.FC<{ event: ActivityEvent }> = ({ event }) => {
  const color = EVENT_COLORS[event.type] ?? '#4a5568';
  const icon = EVENT_ICONS[event.type] ?? '·';
  const label = EVENT_LABELS[event.type] ?? event.type;

  return (
    <div
      className={`flex items-start gap-2 px-3 py-1.5 border-b border-bg-border/40 animate-ticker ${!event.read ? 'bg-bg-elevated/30' : ''}`}
    >
      <span className="font-mono text-xs flex-shrink-0 mt-0.5" style={{ color }}>
        {icon}
      </span>
      <span
        className="font-pixel text-xs px-1 rounded flex-shrink-0"
        style={{ color, background: color + '20', fontSize: '5px' }}
      >
        {label}
      </span>
      <span className="font-mono text-xs text-gray-300 flex-1 leading-relaxed">{event.message}</span>
      <span className="font-mono text-xs text-gray-600 flex-shrink-0">{formatRelative(event.timestamp)}</span>
    </div>
  );
};

export const EventFeed: React.FC = () => {
  const events = useSimulationStore(s => s.events);
  const markEventsRead = useSimulationStore(s => s.markEventsRead);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(events.length);

  // Auto-scroll quand un nouvel event arrive
  useEffect(() => {
    if (events.length !== prevLengthRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      prevLengthRef.current = events.length;
    }
  }, [events.length]);

  return (
    <div className="flex flex-col border-t border-bg-border" style={{ height: '200px' }}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-bg-border bg-bg-panel">
        <span className="font-pixel text-xs text-gray-400">ACTIVITÉ LIVE</span>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-neon-red animate-pulse" />
          <span className="font-mono text-xs text-neon-red">EN DIRECT</span>
          <button
            onClick={markEventsRead}
            className="font-mono text-xs text-gray-600 hover:text-gray-400 ml-2"
          >
            Tout lire
          </button>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin">
        {events.map(event => (
          <EventRow key={event.id} event={event} />
        ))}
        {events.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-600 font-mono text-xs">
            En attente d'activité...
          </div>
        )}
      </div>
    </div>
  );
};
